/**
 * Resolve the auth state on a cold app load.
 *
 * getUser() only reads persisted storage; it never contacts Keycloak. The
 * access token lifespan is short (1 hour in the frontend-healthos client), so
 * after any gap longer than that with no open tab running automaticSilentRenew,
 * the stored access token is already expired on load even though the refresh
 * token (and the Keycloak session) are still valid for days. Treating that as
 * signed-out throws away a fully recoverable session and bounces the user to a
 * sign-in redirect.
 *
 * So when the stored user is expired, attempt a silent renew (refresh-token
 * grant) before deciding. This mirrors getFreshBearerToken, which already does
 * the same thing for imperative API calls. If renewal genuinely fails we still
 * fall through to signedOut, so there is no zombie-session regression.
 */
export async function resolveInitialAuthState(manager) {
    let user = await manager.getUser();
    if (user?.expired) {
        user = await manager.signinSilent().catch(() => null);
        if (!user || user.expired) {
            // The renew failed, but it can fail simply because a concurrent renew
            // already consumed the rotating refresh token: React Strict Mode's
            // double effect, or an overlapping getFreshBearerToken call. That
            // sibling renew may have just written a fresh user to storage, so
            // re-read it before giving up rather than signing out a session that is
            // actually valid.
            user = await manager.getUser();
        }
    }
    return user && !user.expired ? { status: "signedIn", user } : { status: "signedOut" };
}
//# sourceMappingURL=session.js.map